
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { useNavigationUtils } from '@/hooks/useNavigationUtils';

interface ServicesDropdownProps {
  isServicesActive: boolean;
}

const ServicesDropdown: React.FC<ServicesDropdownProps> = ({ isServicesActive }) => {
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const { servicePages, isServiceActive, getServicePath } = useNavigationUtils();

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target as Node)) {
        setIsServicesDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleServicesDropdown = () => {
    setIsServicesDropdownOpen(!isServicesDropdownOpen);
  };

  return (
    <div className="relative group" ref={servicesDropdownRef}>
      <button 
        onClick={toggleServicesDropdown}
        className={`flex items-center ${isServicesActive ? 'text-purple-400' : 'text-gray-100 hover:text-purple-400'} transition-colors font-future tracking-wide text-sm`}
        aria-expanded={isServicesDropdownOpen}
        aria-controls="services-dropdown"
      >
        {t.services} <ChevronDown size={16} className={`ml-1 transform transition-transform duration-300 ${isServicesDropdownOpen ? 'rotate-180' : ''}`} />
      </button>
      <div 
        id="services-dropdown"
        className={`absolute left-0 mt-2 w-48 rounded-md shadow-lg bg-[#1A1F2C]/95 ring-1 ring-purple-900/30 ring-opacity-5 transition-all duration-200 backdrop-blur-lg z-50 ${
          isServicesDropdownOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <div className="py-1 relative overflow-hidden">
          {/* Light scan effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-purple-500/10 to-transparent w-full h-full translate-x-full group-hover:animate-scan-line"></div>
          
          {servicePages.map((service) => {
            const servicePath = getServicePath(service.path);
            return (
              <Link 
                key={service.key}
                to={servicePath}
                onClick={() => setIsServicesDropdownOpen(false)}
                className={`block px-4 py-2 text-sm ${isServiceActive(service.path) ? 'text-purple-400 bg-purple-900/30' : 'text-gray-200 hover:bg-purple-900/20 hover:text-purple-400'} font-future tracking-wide relative group`}
              >
                <span>{t[service.key]}</span>
                <span className="absolute bottom-0 left-4 right-4 h-[1px] bg-gradient-to-r from-transparent via-purple-500/30 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ServicesDropdown;
